import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Search, Plus, Eye, KeyRound, UserX, Shield, Camera, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HR_WRITE, ROLE_LABELS, ROLE_BADGE, EMP_STATUS, fmtDate } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { EmployeeDocuments } from "@/components/EmployeeDocuments";

const empty = {
  full_name: "", nik: "", department_id: "", position_id: "", employment_status: "TETAP",
  join_date: "", exit_date: "", work_location: "Showty HQ", work_schedule: "11:00 - 20:00",
  phone: "", email: "", role: "EMPLOYEE", initial_password: "", avatar: "",
  gender: "", birth_place: "", birth_date: "", marital_status: "", religion: "",
  education: "", major: "", university: "", area: "",
  address_ktp: "", address_domicile: "",
  emergency_contact_name: "", emergency_contact_phone: "", emergency_contact_relation: "",
  bank_name: "", bank_branch: "", bank_account: "",
};

const GENDER = [{ v: "L", l: "Laki-laki" }, { v: "P", l: "Perempuan" }];
const MARITAL = ["Belum Menikah", "Menikah", "Cerai"].map((x) => ({ v: x, l: x }));
const RELIGION = ["Islam", "Kristen", "Katolik", "Hindu", "Buddha", "Konghucu", "Lainnya"].map((x) => ({ v: x, l: x }));

export default function Employees() {
  const { user } = useAuth();
  const canWrite = HR_WRITE.includes(user.role);
  const [list, setList] = useState([]);
  const [depts, setDepts] = useState([]);
  const [positions, setPositions] = useState([]);
  const [q, setQ] = useState("");
  const [deptFilter, setDeptFilter] = useState("ALL");
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [fullNik, setFullNik] = useState("");
  const [resetFor, setResetFor] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [deleteFor, setDeleteFor] = useState(null);

  const load = useCallback(() => {
    const p = {};
    if (q) p.q = q;
    if (deptFilter !== "ALL") p.department_id = deptFilter;
    api.get("/employees", { params: p }).then((r) => setList(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [q, deptFilter]);

  useEffect(() => {
    api.get("/departments").then((r) => setDepts(r.data)).catch(() => {});
    api.get("/positions").then((r) => setPositions(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => setForm({ ...empty });
  const openEdit = (e) => setForm({ ...e, initial_password: "", nik: "" });

  const save = async () => {
    try {
      if (form.id) {
        const { initial_password, nik, ...body } = form;
        await api.put(`/employees/${form.id}`, body);
        toast.success("Data karyawan diperbarui");
      } else {
        await api.post("/employees", form);
        toast.success("Karyawan & akun HRIS berhasil dibuat");
      }
      setForm(null);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openDetail = async (e) => {
    setFullNik("");
    const { data } = await api.get(`/employees/${e.id}`);
    setDetail(data);
  };

  const uploadPhoto = (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Ukuran foto maksimal 5MB");
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = async () => {
        const max = 256;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
        try {
          await api.put(`/employees/${detail.id}`, { avatar: dataUrl });
          toast.success("Foto profil diperbarui");
          setDetail({ ...detail, avatar: dataUrl });
          load();
        } catch (e) { toast.error(apiErr(e)); }
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const pickFormPhoto = (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Ukuran foto maksimal 5MB");
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const max = 256;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        setForm((prev) => ({ ...prev, avatar: canvas.toDataURL("image/jpeg", 0.7) }));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const revealNik = async () => {
    try {
      const { data } = await api.get(`/employees/${detail.id}/nik`);
      setFullNik(data.nik);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doReset = async () => {
    try {
      await api.post(`/employees/${resetFor.id}/reset-password`, { new_password: resetPw });
      toast.success("Kata sandi direset. Karyawan wajib ganti saat login.");
      setResetFor(null); setResetPw("");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/employees/${deleteFor.id}`);
      toast.success("Karyawan dihapus permanen");
      if (detail?.id === deleteFor.id) setDetail(null);
      setDeleteFor(null);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const changeStatus = async (emp, employment_status) => {
    try {
      await api.put(`/employees/${emp.id}/status`, { employment_status });
      toast.success("Status kepegawaian diperbarui");
      load();
      if (detail?.id === emp.id) openDetail(emp);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight lg:text-3xl">Database Karyawan</h1>
          <p className="mt-1 text-sm text-slate-500">Kelola data & akun karyawan PT Showty Global International.</p>
        </div>
        {canWrite && (
          <Button data-testid="employee-add-button" onClick={openNew} className="gap-2">
            <Plus className="h-4 w-4" /> Tambah Karyawan
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="employee-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Cari nama atau ID karyawan..." className="pl-10" />
        </div>
        <Select value={deptFilter} onValueChange={setDeptFilter}>
          <SelectTrigger data-testid="employee-dept-filter" className="w-56"><SelectValue placeholder="Semua Departemen" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Semua Departemen</SelectItem>
            {depts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="rounded-2xl border-slate-200 dark:border-slate-800">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Karyawan</TableHead>
                <TableHead>ID / NIK</TableHead>
                <TableHead>Departemen</TableHead>
                <TableHead>Jabatan</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.length === 0 && (
                <TableRow><TableCell colSpan={7} className="py-8 text-center text-slate-400">Tidak ada karyawan.</TableCell></TableRow>
              )}
              {list.map((e) => (
                <TableRow key={e.id} data-testid="employee-row">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9">
                        <AvatarImage src={e.avatar} />
                        <AvatarFallback className="bg-primary/15 text-xs text-primary">{e.full_name?.[0]}</AvatarFallback>
                      </Avatar>
                      <span className="font-medium">{e.full_name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="font-mono text-xs">{e.employee_id}</p>
                    <p className="font-mono text-[11px] text-slate-400">{e.nik_masked}</p>
                  </TableCell>
                  <TableCell className="text-sm">{e.department_name || "-"}</TableCell>
                  <TableCell className="text-sm">{e.position_name || "-"}</TableCell>
                  <TableCell>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${ROLE_BADGE[e.role]}`}>{ROLE_LABELS[e.role]}</span>
                  </TableCell>
                  <TableCell>
                    <span className={`text-xs font-medium ${["RESIGN", "TERMINATED", "INACTIVE"].includes(e.employment_status) ? "text-rose-600" : "text-emerald-600"}`}>
                      {e.employment_status}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button data-testid="employee-view-button" size="icon" variant="ghost" onClick={() => openDetail(e)}><Eye className="h-4 w-4" /></Button>
                      {canWrite && <>
                        <Button data-testid="employee-edit-button" size="sm" variant="ghost" onClick={() => openEdit(e)}>Edit</Button>
                        <Button data-testid="employee-reset-button" size="icon" variant="ghost" title="Reset Kata Sandi" onClick={() => setResetFor(e)}><KeyRound className="h-4 w-4" /></Button>
                        {e.role !== "SECRETARIAT" && <Button data-testid="employee-delete-button" size="icon" variant="ghost" title="Hapus Karyawan" onClick={() => setDeleteFor(e)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>}
                      </>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle>{form?.id ? "Edit Karyawan" : "Tambah Karyawan Baru"}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={form.avatar} />
                  <AvatarFallback className="bg-primary/15 text-primary">{form.full_name?.[0] || "?"}</AvatarFallback>
                </Avatar>
                <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200">
                  <Camera className="h-3.5 w-3.5" /> Upload Foto
                  <input type="file" accept="image/*" className="hidden" data-testid="emp-photo-input" onChange={(e) => pickFormPhoto(e.target.files)} />
                </label>
              </div>

              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Data Utama</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Nama Lengkap" testid="emp-name-input" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} />
                {!form.id && <Field label="NIK KTP (16 digit)" testid="emp-nik-input" value={form.nik} onChange={(v) => setForm({ ...form, nik: v })} mono />}
                <SelectField label="Departemen / Divisi" testid="emp-dept-select" value={form.department_id} onChange={(v) => setForm({ ...form, department_id: v })} options={depts.map((d) => ({ v: d.id, l: d.name }))} />
                <SelectField label="Jabatan" testid="emp-position-select" value={form.position_id} onChange={(v) => setForm({ ...form, position_id: v })} options={positions.map((p) => ({ v: p.id, l: p.name }))} />
                <SelectField label="Role Akses" testid="emp-role-select" value={form.role} onChange={(v) => setForm({ ...form, role: v })} options={Object.keys(ROLE_LABELS).map((r) => ({ v: r, l: ROLE_LABELS[r] }))} />
                <SelectField label="Status Kepegawaian" testid="emp-status-select" value={form.employment_status} onChange={(v) => setForm({ ...form, employment_status: v })} options={EMP_STATUS.map((s) => ({ v: s, l: s }))} />
              </div>

              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Data Pribadi</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <SelectField label="Jenis Kelamin (Gender)" testid="emp-gender-select" value={form.gender} onChange={(v) => setForm({ ...form, gender: v })} options={GENDER} />
                <Field label="Tempat Lahir (Place of Birth)" value={form.birth_place} onChange={(v) => setForm({ ...form, birth_place: v })} />
                <Field label="Tanggal Lahir (Date of Birth)" type="date" value={form.birth_date || ""} onChange={(v) => setForm({ ...form, birth_date: v })} />
                <SelectField label="Status Perkawinan (Marital)" value={form.marital_status} onChange={(v) => setForm({ ...form, marital_status: v })} options={MARITAL} />
                <SelectField label="Agama (Religion)" value={form.religion} onChange={(v) => setForm({ ...form, religion: v })} options={RELIGION} />
                <Field label="Pendidikan (Education)" value={form.education} onChange={(v) => setForm({ ...form, education: v })} />
                <Field label="Jurusan (Major)" value={form.major} onChange={(v) => setForm({ ...form, major: v })} />
                <Field label="Universitas (University)" value={form.university} onChange={(v) => setForm({ ...form, university: v })} />
                <Field label="No. Kontak (Contact Number)" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                <div className="sm:col-span-2"><Field label="Alamat 1 - sesuai KTP (Address 1)" value={form.address_ktp} onChange={(v) => setForm({ ...form, address_ktp: v })} /></div>
                <div className="sm:col-span-2"><Field label="Alamat 2 - domisili saat ini (Address 2)" value={form.address_domicile} onChange={(v) => setForm({ ...form, address_domicile: v })} /></div>
                <Field label="Kontak Darurat - No. (Emergency Number)" value={form.emergency_contact_name} onChange={(v) => setForm({ ...form, emergency_contact_name: v })} />
                <Field label="Kontak Darurat - Telepon" value={form.emergency_contact_phone} onChange={(v) => setForm({ ...form, emergency_contact_phone: v })} />
                <Field label="Kontak Darurat - Hubungan (Relation)" value={form.emergency_contact_relation} onChange={(v) => setForm({ ...form, emergency_contact_relation: v })} />
              </div>

              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Kepegawaian & Bank</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Join Date" type="date" value={form.join_date || ""} onChange={(v) => setForm({ ...form, join_date: v })} />
                <Field label="Exit Date" type="date" value={form.exit_date || ""} onChange={(v) => setForm({ ...form, exit_date: v })} />
                <Field label="Area" value={form.area} onChange={(v) => setForm({ ...form, area: v })} />
                <Field label="Lokasi Kerja" value={form.work_location} onChange={(v) => setForm({ ...form, work_location: v })} />
                <Field label="Bank" value={form.bank_name} onChange={(v) => setForm({ ...form, bank_name: v })} />
                <Field label="KCU Bank (Cabang)" value={form.bank_branch} onChange={(v) => setForm({ ...form, bank_branch: v })} />
                <Field label="No. Rekening (Account Bank)" value={form.bank_account} onChange={(v) => setForm({ ...form, bank_account: v })} mono />
                <SelectField label="Supervisor" value={form.supervisor_id || "NONE"} onChange={(v) => setForm({ ...form, supervisor_id: v === "NONE" ? null : v })} options={[{ v: "NONE", l: "Tidak ada" }, ...list.filter((x) => x.id !== form.id).map((x) => ({ v: x.id, l: x.full_name }))]} />
                {!form.id && <Field label="Kata Sandi Awal" testid="emp-password-input" value={form.initial_password} onChange={(v) => setForm({ ...form, initial_password: v })} />}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Batal</Button>
            <Button data-testid="employee-save-button" onClick={save}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Profil Karyawan</DialogTitle></DialogHeader>
          {detail && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
              <div className="flex items-center gap-4">
                {canWrite ? (
                  <label className="relative cursor-pointer" data-testid="change-photo-label" title="Ganti foto profil">
                    <Avatar className="h-16 w-16">
                      <AvatarImage src={detail.avatar} />
                      <AvatarFallback className="bg-primary/15 text-primary">{detail.full_name?.[0]}</AvatarFallback>
                    </Avatar>
                    <input type="file" accept="image/*" className="hidden" data-testid="photo-upload-input" onChange={(e) => uploadPhoto(e.target.files)} />
                    <span className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-white shadow"><Camera className="h-3.5 w-3.5" /></span>
                  </label>
                ) : (
                  <Avatar className="h-16 w-16">
                    <AvatarImage src={detail.avatar} />
                    <AvatarFallback className="bg-primary/15 text-primary">{detail.full_name?.[0]}</AvatarFallback>
                  </Avatar>
                )}
                <div>
                  <p className="font-heading text-lg font-bold">{detail.full_name}</p>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${ROLE_BADGE[detail.role]}`}>{ROLE_LABELS[detail.role]}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="ID Karyawan" value={detail.employee_id} mono />
                <Info label="NIK" value={fullNik || detail.nik_masked} mono
                  extra={canWrite && !fullNik && (
                    <button data-testid="reveal-nik-button" onClick={revealNik} className="ml-2 inline-flex items-center gap-1 text-xs text-primary hover:underline">
                      <Shield className="h-3 w-3" /> Lihat
                    </button>
                  )} />
                <Info label="Departemen" value={detail.department_name} />
                <Info label="Jabatan" value={detail.position_name} />
                <Info label="Status" value={detail.employment_status} />
                <Info label="Bergabung" value={fmtDate(detail.join_date)} />
                <Info label="Telepon" value={detail.phone} />
                <Info label="Email" value={detail.email || "-"} />
                <Info label="Lokasi Kerja" value={detail.work_location} />
                <Info label="Jadwal" value={detail.work_schedule} />
                <Info label="Area" value={detail.area} />
                <Info label="Jenis Kelamin" value={detail.gender === "L" ? "Laki-laki" : detail.gender === "P" ? "Perempuan" : "-"} />
                <Info label="Tempat, Tgl Lahir" value={detail.birth_place ? `${detail.birth_place}, ${detail.birth_date ? fmtDate(detail.birth_date) : "-"}` : "-"} />
                <Info label="Status Perkawinan" value={detail.marital_status} />
                <Info label="Agama" value={detail.religion} />
                <Info label="Pendidikan" value={detail.education} />
                <Info label="Jurusan" value={detail.major} />
                <Info label="Universitas" value={detail.university} />
                <Info label="Exit Date" value={detail.exit_date ? fmtDate(detail.exit_date) : "-"} />
                <Info label="Kontak Darurat" value={detail.emergency_contact_name ? `${detail.emergency_contact_name} (${detail.emergency_contact_phone || "-"}${detail.emergency_contact_relation ? " · " + detail.emergency_contact_relation : ""})` : "-"} />
                <Info label="Bank" value={detail.bank_name ? `${detail.bank_name}${detail.bank_branch ? " KCU " + detail.bank_branch : ""} - ${detail.bank_account || "-"}` : "-"} />
                <div className="col-span-2"><Info label="Alamat KTP" value={detail.address_ktp} /></div>
                <div className="col-span-2"><Info label="Alamat Domisili" value={detail.address_domicile} /></div>
                <Info label="Status Akun" value={detail.account_status} />
              </div>
              <EmployeeDocuments empId={detail.id} canWrite={canWrite} />
              {canWrite && (
                <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4 dark:border-slate-800">
                  <Button size="sm" variant="destructive" data-testid="offboard-button" onClick={() => changeStatus(detail, "RESIGN")} className="gap-1">
                    <UserX className="h-4 w-4" /> Nonaktifkan (Resign)
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => changeStatus(detail, "TETAP")}>Aktifkan Kembali</Button>
                </div>
              )}
            </motion.div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!deleteFor} onOpenChange={(o) => !o && setDeleteFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Hapus Karyawan</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Yakin menghapus <span className="font-semibold">{deleteFor?.full_name}</span>? Tindakan ini permanen — data karyawan, akun login, absensi, dan formulir terkait akan dihapus.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteFor(null)}>Batal</Button>
            <Button variant="destructive" data-testid="confirm-delete-button" onClick={doDelete}>Hapus Permanen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetFor} onOpenChange={(o) => !o && setResetFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset Kata Sandi</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">{resetFor?.full_name} — karyawan wajib ganti kata sandi saat login berikutnya.</p>
          <div className="space-y-2">
            <Label>Kata Sandi Baru</Label>
            <Input data-testid="reset-password-input" value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="Minimal 6 karakter" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetFor(null)}>Batal</Button>
            <Button data-testid="confirm-reset-button" onClick={doReset}>Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const slug = (s) => "field-" + s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function Field({ label, value, onChange, type = "text", testid, mono }) {
  const fid = testid || slug(label);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={fid} className="text-xs">{label}</Label>
      <Input id={fid} data-testid={fid} type={type} value={value} onChange={(e) => onChange(e.target.value)} className={mono ? "font-mono" : ""} />
    </div>
  );
}
function SelectField({ label, value, onChange, options, testid }) {
  const fid = testid || slug(label);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={fid} className="text-xs">{label}</Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger id={fid} data-testid={fid}><SelectValue placeholder="Pilih..." /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
function Info({ label, value, mono, extra }) {
  return (
    <div>
      <p className="text-xs text-slate-400">{label}</p>
      <p className={`font-medium ${mono ? "font-mono" : ""}`}>{value || "-"}{extra}</p>
    </div>
  );
}
